import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileQuestion } from "lucide-react";
import EventPage from "./EventPage";
import { ASNLoader } from "./ASNLoader";
import { Button } from "./ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Block Renderer Components
const HeroBlock = ({ content }) => (
  <section 
    className="relative min-h-[400px] flex items-center justify-center bg-cover bg-center"
    style={{ backgroundImage: content.background_image ? `url(${content.background_image})` : undefined }}
  >
    {content.background_image && <div className="absolute inset-0 bg-black/60" />}
    <div className="relative text-center px-4 py-16 max-w-4xl mx-auto">
      <h1 className="font-heading text-5xl md:text-7xl text-white uppercase tracking-tight mb-4">
        {content.title}
      </h1>
      {content.subtitle && (
        <p className="text-xl text-zinc-300 mb-8">{content.subtitle}</p>
      )}
      {content.cta_text && content.cta_link && (
        <Link to={content.cta_link}>
          <Button className="bg-white text-black hover:bg-zinc-200 uppercase font-bold px-8 py-3">
            {content.cta_text} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      )}
    </div>
  </section>
);

const TextBlock = ({ content }) => (
  <section className="py-12">
    <div className="max-w-4xl mx-auto px-4 md:px-8">
      {content.heading && (
        <h2 className="font-heading text-3xl md:text-4xl text-white uppercase tracking-tight mb-6">
          {content.heading}
        </h2>
      )}
      {content.body && (
        <div 
          className="prose prose-invert prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: content.body }}
        />
      )}
    </div>
  </section>
);

const ImageBlock = ({ content }) => (
  <section className="py-8">
    <div className={`mx-auto px-4 md:px-8 ${
      content.size === "full" ? "max-w-[1400px]" :
      content.size === "medium" ? "max-w-4xl" :
      "max-w-2xl"
    }`}>
      {content.src && (
        <figure>
          <img 
            src={content.src} 
            alt={content.alt || ""} 
            className="w-full h-auto"
          />
          {content.caption && (
            <figcaption className="text-center text-zinc-500 text-sm mt-3">
              {content.caption}
            </figcaption>
          )}
        </figure>
      )}
    </div>
  </section>
);

const VideoBlock = ({ content }) => {
  // Extract video ID from YouTube/Vimeo URLs
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    
    return url;
  };

  const embedUrl = getEmbedUrl(content.url);

  return (
    <section className="py-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {content.title && (
          <h3 className="font-heading text-2xl text-white mb-4">{content.title}</h3>
        )}
        {embedUrl && (
          <div className="aspect-video bg-zinc-900">
            <iframe
              src={embedUrl}
              title={content.title || "Video"}
              className="w-full h-full"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        )}
      </div>
    </section>
  );
};

const GalleryBlock = ({ content }) => (
  <section className="py-8">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8">
      <div className={`grid gap-4 ${
        content.columns === 4 ? "grid-cols-2 md:grid-cols-4" :
        content.columns === 2 ? "grid-cols-1 md:grid-cols-2" :
        "grid-cols-1 md:grid-cols-3"
      }`}>
        {content.images?.map((img, index) => (
          <div key={index} className="aspect-video bg-zinc-900 overflow-hidden">
            <img 
              src={img.src} 
              alt={img.alt || ""} 
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CTABlock = ({ content }) => (
  <section className="py-16 bg-zinc-900">
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
      {content.heading && (
        <h2 className="font-heading text-4xl text-white uppercase tracking-tight mb-4">
          {content.heading}
        </h2>
      )}
      {content.text && (
        <p className="text-zinc-400 text-lg mb-8">{content.text}</p>
      )}
      <div className="flex flex-wrap items-center justify-center gap-4">
        {content.buttons?.map((btn, index) => (
          <Link key={index} to={btn.link || "#"}>
            <Button className={
              btn.style === "primary" 
                ? "bg-white text-black hover:bg-zinc-200 uppercase font-bold px-8"
                : "bg-transparent border border-white text-white hover:bg-white hover:text-black uppercase font-bold px-8"
            }>
              {btn.text}
            </Button>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const ColumnsBlock = ({ content }) => (
  <section className="py-12">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8">
      <div className={`grid gap-8 ${
        content.columns?.length === 4 ? "grid-cols-1 md:grid-cols-4" :
        content.columns?.length === 3 ? "grid-cols-1 md:grid-cols-3" :
        "grid-cols-1 md:grid-cols-2"
      }`}>
        {content.columns?.map((col, index) => (
          <div key={index}>
            {col.heading && (
              <h3 className="font-heading text-xl text-white uppercase mb-4">{col.heading}</h3>
            )}
            {col.content && (
              <div 
                className="prose prose-invert prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: col.content }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HTMLBlock = ({ content }) => (
  <section className="py-8">
    <div 
      className="max-w-[1400px] mx-auto px-4 md:px-8"
      dangerouslySetInnerHTML={{ __html: content.html || "" }}
    />
  </section>
);

// Block type to component map
const BLOCK_COMPONENTS = {
  hero: HeroBlock,
  text: TextBlock,
  image: ImageBlock,
  video: VideoBlock,
  gallery: GalleryBlock,
  cta: CTABlock,
  columns: ColumnsBlock,
  html: HTMLBlock
};

export const CustomPageRenderer = () => {
  const { slug, "*": wildcardPath } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine the path to look up
  const pathToLookup = wildcardPath || slug || location.pathname.slice(1);

  useEffect(() => {
    let isMounted = true;
    
    const fetchContent = async () => {
      if (!isMounted) return;
      setLoading(true);
      setError(null);
      setPage(null);
      setEvent(null);
      
      // First try to find a custom page
      try {
        const pageRes = await axios.get(`${API}/pages/custom/${pathToLookup}`);
        if (isMounted) {
          setPage(pageRes.data);
          document.title = `ASN | ${pageRes.data.title || 'Page'}`;
          setLoading(false);
        }
        return;
      } catch (pageErr) {
        // Page not found, try event
      }
      
      // Then try to find an event by custom_url_path
      try {
        const eventRes = await axios.get(`${API}/events/by-path/${pathToLookup}`);
        if (isMounted) {
          setEvent(eventRes.data);
          document.title = `ASN | ${eventRes.data.name || 'Event'}`;
          setLoading(false);
        }
        return;
      } catch (eventErr) {
        // Event not found either
      }
      
      // Nothing found
      if (isMounted) {
        setError("Page not found");
        setLoading(false);
      }
    };

    fetchContent();
    
    return () => {
      isMounted = false;
    };
  }, [pathToLookup]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 flex items-center justify-center min-h-[60vh]">
          <ASNLoader size="large" />
        </div>
      </main>
    );
  }

  if (error || (!page && !event)) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 flex flex-col items-center justify-center min-h-[60vh]">
          <FileQuestion className="w-16 h-16 text-zinc-700 mb-4" />
          <h1 className="font-heading text-2xl text-white mb-2">Page Not Found</h1>
          <p className="text-zinc-500 mb-6">The page you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/">
            <Button className="bg-white text-black hover:bg-zinc-200">
              Go Home
            </Button>
          </Link>
        </div>
        
      </main>
    );
  }

  // If we found an event, render EventPage with prefetched data
  if (event) {
    return <EventPage prefetchedEvent={event} />;
  }

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="custom-page">
      
      

      <div className="pt-8">
        {/* Render content blocks */}
        {page.content_blocks?.map((block, index) => {
          const BlockComponent = BLOCK_COMPONENTS[block.type];
          if (!BlockComponent) return null;
          
          return (
            <motion.div
              key={block.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <BlockComponent content={block.content || {}} />
            </motion.div>
          );
        })}

        {/* Show title if no blocks */}
        {(!page.content_blocks || page.content_blocks.length === 0) && (
          <section className="py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
              <h1 className="font-heading text-4xl text-white uppercase tracking-tight">
                {page.title}
              </h1>
              {page.description && (
                <p className="text-zinc-400 mt-4">{page.description}</p>
              )}
            </div>
          </section>
        )}
      </div>

      
    </main>
  );
};

export default CustomPageRenderer;
